import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { GET_TASK_POOL, QUOTE_FROM_HASH, GET_QUOTE } from '../queries';
import { CoreTaskStatusChoices, GetTaskPoolQuery } from '../gql/graphql';
import { StoreState } from '../store';
import {
  QuoteWizardQuote,
  QuoteWizardAnsweredForm,
} from '../components/QuoteWizard/classes';
import QuoteWizardState from '../store/QuoteWizardState';
import type { BusinessType } from '../Typescript';
import { extractQuoteStatus } from '../util';
import { getAuthTokenFromLocalStorage } from '../components/Authentication';

const POLLING_INTERVAL_MS = 500;

type TaskPool = NonNullable<GetTaskPoolQuery['getTaskPool']>;

const isTaskPoolFinished = (taskPool: TaskPool) => {
  if (!taskPool.tasks || taskPool.tasks.length === 0) {
    return false;
  }

  return taskPool.tasks.every((task) => {
    if (!task) {
      return false;
    }

    return (
      task.status === CoreTaskStatusChoices.Completed ||
      task.status === CoreTaskStatusChoices.Failed
    );
  });
};

const parseTaskPoolAdditionalInformation = (
  taskPool: TaskPool,
): Record<string, unknown> | null => {
  if (!taskPool.additionalInformation) {
    return null;
  }

  try {
    const additionalInfo = JSON.parse(taskPool.additionalInformation);

    if (!additionalInfo || typeof additionalInfo !== 'object') {
      return null;
    }

    return additionalInfo;
  } catch {
    return null;
  }
};

const getQuoteUuidFromTaskPool = (taskPool: TaskPool): string | null => {
  const additionalInfo = parseTaskPoolAdditionalInformation(taskPool);
  const quoteUuid = additionalInfo?.quote_uuid;

  if (typeof quoteUuid === 'string' && quoteUuid.trim() !== '') {
    return quoteUuid;
  }

  return null;
};

const getQuoteCorrespondenceHashFromTaskPool = (
  taskPool: TaskPool,
): string | null => {
  const additionalInfo = parseTaskPoolAdditionalInformation(taskPool);
  const quoteCorrespondenceHash = additionalInfo?.quote_correspondence_hash;

  if (
    typeof quoteCorrespondenceHash === 'string' &&
    quoteCorrespondenceHash.trim() !== ''
  ) {
    return quoteCorrespondenceHash;
  }

  return null;
};

export interface UseQuoteSubmissionOptions {
  active?: boolean;
  onComplete?: () => void;
}

export function useQuoteSubmission({
  active = true,
  onComplete,
}: UseQuoteSubmissionOptions = {}) {
  const dispatch = useDispatch();
  const taskPoolId = useSelector(
    (state: StoreState) => state.quoteWizard.quoteSubmissionTaskPoolId,
  );
  const formsWithAnswers = useSelector(
    (state: StoreState) => state.quoteWizard.formAnswers,
  );
  const selectedBusinessType = useSelector(
    (state: StoreState) => state.quoteWizard.selectedBusinessType,
  );

  const [
    getTaskPool,
    {
      data: taskPoolData,
      error: taskPoolError,
      loading: taskPoolLoading,
      stopPolling,
      startPolling,
      called,
    },
  ] = useLazyQuery(GET_TASK_POOL, {
    pollInterval: POLLING_INTERVAL_MS,
  });

  const [
    getQuoteFromHash,
    { data: quoteHashData, error: quoteError, loading: quoteLoading },
  ] = useLazyQuery(QUOTE_FROM_HASH);

  const [
    getQuote,
    { data: quoteData, error: getQuoteError, loading: getQuoteLoading },
  ] = useLazyQuery(GET_QUOTE);

  const hasCompletedRef = useRef(false);
  const awaitingQuoteDataRef = useRef(false);

  type SubmittedQuoteResponse = NonNullable<
    Parameters<typeof extractQuoteStatus>[0]
  > & {
    uniqueID?: string | number | null;
  };

  const storeSubmittedQuote = useCallback(
    (quoteResponse: SubmittedQuoteResponse): boolean => {
      const businessType: BusinessType | undefined = selectedBusinessType
        ? 'id' in selectedBusinessType
          ? selectedBusinessType
          : selectedBusinessType.original
        : undefined;

      if (!businessType) {
        return false;
      }

      const status = extractQuoteStatus(quoteResponse);
      const quoteWizardQuote = new QuoteWizardQuote({
        ...quoteResponse,
        status,
      });

      const formsWithAnswersArray: Array<QuoteWizardAnsweredForm> =
        Object.values(formsWithAnswers);

      dispatch(
        QuoteWizardState.actions.markQuoteAsSubmitted({
          formsWithAnswers: formsWithAnswersArray,
          quote: quoteWizardQuote,
          businessType,
        }),
      );

      if ('uniqueID' in quoteResponse && quoteResponse.uniqueID) {
        const uuid =
          typeof quoteResponse.uniqueID === 'string'
            ? quoteResponse.uniqueID
            : String(quoteResponse.uniqueID);
        dispatch(
          QuoteWizardState.actions.setCurrentQuoteUUID({
            uuid,
          }),
        );
      }

      return true;
    },
    [dispatch, formsWithAnswers, selectedBusinessType],
  );

  const completeSubmission = useCallback(() => {
    if (hasCompletedRef.current) {
      return;
    }

    hasCompletedRef.current = true;
    awaitingQuoteDataRef.current = false;
    stopPolling?.();

    if (onComplete) {
      onComplete();
    }

    dispatch(QuoteWizardState.actions.setQuoteSubmissionTaskPoolId(null));
  }, [dispatch, onComplete, stopPolling]);

  useEffect(() => {
    hasCompletedRef.current = false;
    awaitingQuoteDataRef.current = false;
  }, [taskPoolId]);

  // Start polling when taskPoolId is available and active is true
  useEffect(() => {
    if (!active) {
      stopPolling?.();
      return;
    }

    if (taskPoolId) {
      getTaskPool({
        variables: { taskPoolId },
      });
      startPolling?.(POLLING_INTERVAL_MS);
    }

    // Stop polling on unmount or when active becomes false
    return () => {
      stopPolling?.();
    };
  }, [taskPoolId, getTaskPool, startPolling, stopPolling, active]);

  // Stop polling if there's an error
  useEffect(() => {
    const taskPool = taskPoolData?.getTaskPool;
    const hasError =
      taskPoolError || quoteError || getQuoteError || taskPool?.error;

    if (hasError && called) {
      stopPolling?.();
    }
  }, [
    taskPoolError,
    quoteError,
    getQuoteError,
    taskPoolData?.getTaskPool?.error,
    called,
    stopPolling,
  ]);

  // When the task pool finishes, fetch quote data via hash or UUID
  useEffect(() => {
    if (!active || hasCompletedRef.current || awaitingQuoteDataRef.current) {
      return;
    }

    const taskPool = taskPoolData?.getTaskPool;
    if (!taskPool || !isTaskPoolFinished(taskPool)) {
      return;
    }

    const quoteCorrespondenceHash =
      getQuoteCorrespondenceHashFromTaskPool(taskPool);
    if (quoteCorrespondenceHash) {
      awaitingQuoteDataRef.current = true;
      stopPolling?.();
      getQuoteFromHash({
        variables: {
          hash: quoteCorrespondenceHash,
          delete: false,
        },
      });
      return;
    }

    const quoteUuid = getQuoteUuidFromTaskPool(taskPool);
    if (quoteUuid) {
      awaitingQuoteDataRef.current = true;
      stopPolling?.();
      getQuote({
        variables: {
          uID: quoteUuid,
          token: getAuthTokenFromLocalStorage(),
        },
      });
    }
  }, [taskPoolData, active, getQuoteFromHash, getQuote, stopPolling]);

  // Handle quote data when received from hash
  useEffect(() => {
    if (!active || hasCompletedRef.current) {
      return;
    }

    const quoteFromHash = quoteHashData?.quoteFromHash;
    if (!quoteFromHash) {
      return;
    }

    if (storeSubmittedQuote(quoteFromHash)) {
      completeSubmission();
    }
  }, [quoteHashData, active, storeSubmittedQuote, completeSubmission]);

  // Handle quote data when received via UUID (e.g. resumed Axis quotes)
  useEffect(() => {
    if (!active || hasCompletedRef.current) {
      return;
    }

    const quoteFromUuid = quoteData?.quote;
    if (!quoteFromUuid) {
      return;
    }

    if (storeSubmittedQuote(quoteFromUuid)) {
      completeSubmission();
    }
  }, [quoteData, active, storeSubmittedQuote, completeSubmission]);

  return {
    taskPool: {
      loading: taskPoolLoading,
      error: taskPoolError,
      data: taskPoolData,
    },
    quote: {
      loading: quoteLoading || getQuoteLoading,
      error: quoteError || getQuoteError,
      data: quoteHashData,
    },
  };
}
